import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "theme";
const query = () => window.matchMedia("(prefers-color-scheme: light)");

/** Explicit choice (data-theme) wins; otherwise follow the OS. */
const resolve = () =>
  document.documentElement.dataset.theme || (query().matches ? "light" : "dark");

/**
 * Theme is applied before first paint by an inline script in public/index.html,
 * so this hook only reads and updates it. Storage access is wrapped because it
 * throws in some private modes.
 */
export function useTheme() {
  const [theme, setTheme] = useState(resolve);

  // Track OS changes while the user has not made an explicit choice.
  useEffect(() => {
    const mq = query();
    const onChange = () => {
      if (!document.documentElement.dataset.theme) setTheme(resolve());
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const toggle = useCallback(() => {
    const next = resolve() === "dark" ? "light" : "dark";
    document.documentElement.dataset.theme = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* non-persistent theme is acceptable */
    }
    setTheme(next);
  }, []);

  return [theme, toggle];
}
